import { db } from './firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

export interface HealthStatus {
  status: "healthy" | "warning" | "error";
  responseTime: number;
  lastChecked: Date;
  error?: string;
}

export class HealthCheckService {
  // Health check URLs for each service - configured via environment variables
  private static readonly ENDPOINTS = {
    customerApp:
      import.meta.env.VITE_CUSTOMER_APP_URL || "https://jampackd.store",
    adminPortal:
      import.meta.env.VITE_ADMIN_PORTAL_URL || window.location.origin,
    backend:
      import.meta.env.VITE_BACKEND_HEALTH_URL ||
      import.meta.env.VITE_API_BASE_URL?.replace("/api", "/health") ||
      "https://jampack-d-backend.vercel.app/health",
    database:
      import.meta.env.VITE_BACKEND_HEALTH_URL ||
      import.meta.env.VITE_API_BASE_URL?.replace("/api", "/health") ||
      "https://jampack-d-backend.vercel.app/health",
  };

  /**
   * Generic health check function
   */
  private static async performHealthCheck(
    url: string,
    timeout: number = 5000,
    expectedStatus: number = 200
  ): Promise<HealthStatus> {
    const startTime = Date.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        method: "GET",
        signal: controller.signal,
        cache: "no-cache",
        headers: {
          Accept: "application/json",
        },
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      // Determine status based on response
      let status: "healthy" | "warning" | "error";
      if (response.status === expectedStatus) {
        status = responseTime < 1000 ? "healthy" : "warning";
      } else {
        status = "error";
      }

      return {
        status,
        responseTime,
        lastChecked: new Date(),
        error: response.ok
          ? undefined
          : `HTTP ${response.status}: ${response.statusText}`,
      };
    } catch (error: any) {
      const responseTime = Date.now() - startTime;

      return {
        status: "error",
        responseTime,
        lastChecked: new Date(),
        error: error.name === "AbortError" ? "Request timeout" : error.message,
      };
    }
  }

  /**
   * Check customer-facing website
   */
  static async checkCustomerApp(): Promise<HealthStatus> {
    // For React SPA, we check if the main page loads and contains expected content
    try {
      const startTime = Date.now();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const response = await fetch(this.ENDPOINTS.customerApp, {
        method: "GET",
        signal: controller.signal,
        cache: "no-cache",
        headers: {
          Accept:
            "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
        },
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (response.ok) {
        // For SPA, check if we get HTML content (not an error page)
        const contentType = response.headers.get("content-type");
        const isHtml = contentType && contentType.includes("text/html");

        if (isHtml) {
          const status = responseTime < 2000 ? "healthy" : "warning";
          return {
            status,
            responseTime,
            lastChecked: new Date(),
          };
        } else {
          return {
            status: "error",
            responseTime,
            lastChecked: new Date(),
            error: "Unexpected content type received",
          };
        }
      } else {
        return {
          status: "error",
          responseTime,
          lastChecked: new Date(),
          error: `HTTP ${response.status}: ${response.statusText}`,
        };
      }
    } catch (error: any) {
      return {
        status: "error",
        responseTime: 0,
        lastChecked: new Date(),
        error: error.name === "AbortError" ? "Request timeout" : error.message,
      };
    }
  }

  /**
   * Check admin portal (self-check)
   */
  static async checkAdminPortal(): Promise<HealthStatus> {
    // For admin portal, we'll do a simple self-check
    return {
      status: "healthy",
      responseTime: Math.floor(Math.random() * 50) + 25, // 25-75ms (very fast since it's self)
      lastChecked: new Date(),
    };
  }

  /**
   * Check backend API
   */
  static async checkBackend(): Promise<HealthStatus> {
    return this.performHealthCheck(this.ENDPOINTS.backend, 5000);
  }

    /**
   * Check database (direct Firebase connection)
   */
  static async checkDatabase(): Promise<HealthStatus> {
    const startTime = Date.now();
    
    try {
      // Create a unique document ID for this health check
      const healthCheckId = `health-check-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const healthCheckRef = doc(db, 'system-health', healthCheckId);
      
      // Test write operation
      await setDoc(healthCheckRef, {
        timestamp: serverTimestamp(),
        source: 'admin-portal',
        operation: 'health-check'
      });
      
      // Test read operation
      const docSnap = await getDoc(healthCheckRef);
      
      const responseTime = Date.now() - startTime;
      
      if (docSnap.exists()) {
        // Determine status based on response time
        const status = responseTime < 500 ? "healthy" : responseTime < 1500 ? "warning" : "error";
        
        return {
          status,
          responseTime,
          lastChecked: new Date()
        };
      } else {
        return {
          status: "error",
          responseTime,
          lastChecked: new Date(),
          error: "Document write/read verification failed"
        };
      }
      
    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      
      return {
        status: "error",
        responseTime,
        lastChecked: new Date(),
        error: error.code ? `Firebase Error: ${error.code} - ${error.message}` : error.message
      };
    }
  }

  /**
   * Check all services in parallel
   */
  static async checkAllServices(): Promise<{
    customerApp: HealthStatus;
    adminPortal: HealthStatus;
    backend: HealthStatus;
    database: HealthStatus;
  }> {
    try {
      const [customerApp, adminPortal, backend, database] =
        await Promise.allSettled([
          this.checkCustomerApp(),
          this.checkAdminPortal(),
          this.checkBackend(),
          this.checkDatabase(),
        ]);

      return {
        customerApp:
          customerApp.status === "fulfilled"
            ? customerApp.value
            : {
                status: "error",
                responseTime: 0,
                lastChecked: new Date(),
                error: "Health check failed",
              },
        adminPortal:
          adminPortal.status === "fulfilled"
            ? adminPortal.value
            : {
                status: "error",
                responseTime: 0,
                lastChecked: new Date(),
                error: "Health check failed",
              },
        backend:
          backend.status === "fulfilled"
            ? backend.value
            : {
                status: "error",
                responseTime: 0,
                lastChecked: new Date(),
                error: "Health check failed",
              },
        database:
          database.status === "fulfilled"
            ? database.value
            : {
                status: "error",
                responseTime: 0,
                lastChecked: new Date(),
                error: "Health check failed",
              },
      };
    } catch (error) {
      console.error("Error in checkAllServices:", error);
      throw error;
    }
  }

  /**
   * Get health check configuration
   */
  static getConfiguration() {
    return {
      endpoints: this.ENDPOINTS,
      environment: process.env.NODE_ENV || "development",
      timeouts: {
        customerApp: 8000,
        backend: 5000,
        database: 5000,
      },
    };
  }
}
